import { DependencyList, useEffect } from "react";

import { ChatEventName, chatEventEmitter } from "@/common/eventEmmiters/chatEventEmitter";
import {
  CommunicationEventName,
  communicationEventEmitter,
} from "@/common/eventEmmiters/communicationEventEmitter";
import { chatHubService } from "@/common/realtime/chatHubService";
import { CommunicationCountersDto } from "@/core/api/generated/v1";
import * as communicationSlice from "@/store/communication/communicationSlice";

import { useAppSelector, useAppThunkDispatch } from "../redux";

export function useCommunicationCounters(params: {
  isEnabled: boolean;
  deps?: DependencyList;
}): CommunicationCountersDto | undefined {
  const thunkDispatch = useAppThunkDispatch();
  const counters = useAppSelector((x) => x.communication.communication.counters);

  useEffect(() => {
    if (params.isEnabled && !counters) {
      thunkDispatch(communicationSlice.getCountersThrottle());
    }
  }, [params.isEnabled, counters, ...(params?.deps || [])]);

  // explicitly subscribe on any chat read activities in any chat for current tenant user
  useEffect(() => {
    if (!params.isEnabled) {
      return;
    }

    const resultPromise = chatHubService.subscribeToChatAnyActivityReadAnyMinimal(() => {
      thunkDispatch(communicationSlice.getCountersThrottle()); // to fetch once in N interval
      thunkDispatch(communicationSlice.getCountersDebounce()); // to fetch in the end of N interval
    });

    return () => {
      resultPromise.then((subscription) => {
        subscription?.unsubscribe();
      });
    };
  }, [params.isEnabled]);

  useEffect(() => {
    if (!params.isEnabled) {
      return;
    }

    // handle when current user reads messages
    const result1 = chatEventEmitter.on2(ChatEventName.MyMessagesMarkedAsRead, () => {
      thunkDispatch(communicationSlice.getCountersDebounce());
    });

    // hack: ensure counters are updated when inbox is opened or fetched
    // const result2 = communicationEventEmitter.on2(CommunicationEventName.InboxOpened, () => {
    //   thunkDispatch(communicationSlice.getCounters());
    // });
    const result3 = communicationEventEmitter.on2(CommunicationEventName.InboxFetched, () => {
      thunkDispatch(communicationSlice.getCounters());
    });
    return () => {
      result1.off();
      // result2.off();
      result3.off();
    };
  }, [params.isEnabled, thunkDispatch]);

  return counters;
}
